import { useEffect, useMemo, useState } from 'react';
import { NotInterested } from '@mui/icons-material';
import { Button, Card, CardContent, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Box, CompanySelect, Headlines, ListItem, LoanSelect, TransactionsLedger } from '/src/components';
import { useDateStore, useFilterStore } from '/src/context';
import { DesktopLayout } from '/src/layouts';
import { Company, Loan } from '/src/lib/models';

/**
 * List Transactions Page
 */
export const ListTransactionsPage = observer(() => {
  /** Context **/
  const filterStore = useFilterStore();
  const dateStore = useDateStore();

  /** State **/
  const [company, setCompany] = useState<Company>();
  const [loan, setLoan] = useState<Loan>();

  /** Computed **/

  const companies = useMemo(
    () => filterStore.companyListGet().filter((c) => (company ? c._id === company._id : true)),
    [filterStore._companyList, company]
  );
  const loans = useMemo(
    () => filterStore.loanListGet().filter((l) => (company ? l.borrower._id === company._id : true)),
    [filterStore._loanList, company]
  );

  /** Methods **/

  const resetFilters = () => {
    setCompany(undefined);
    setLoan(undefined);

    dateStore.setCurrentDate(new Date());
    dateStore.calculateRange();
  };

  /** Effects **/

  useEffect(() => {
    filterStore.lenderSet(undefined);
  }, []);

  /** Render **/
  return (
    <DesktopLayout>
      <Box id="list-transactions">
        <Card>
          <CardContent>
            <Box sx={{ flex: 1 }} layout-align="center center">
              <CompanySelect
                value={company?._id ?? ''}
                setValue={(companyId) => {
                  const company = companies.find((company) => company._id === companyId);
                  setCompany(company);
                  setLoan(undefined);
                }}
                companies={companies}
                ariaLabel="Select a Company"
                placeholder="All Companies"
                enablePlaceholderOption
                boxProps={{ width: '100%' }}
              />

              <Box component="span" flex="5" />

              <LoanSelect
                value={loan?._id ?? ''}
                setValue={(loanId) => {
                  if (!loanId) {
                    resetFilters();
                  } else {
                    const loan = loans.find((loan) => loan._id === loanId);
                    setLoan(loan);
                    setCompany(loan?.borrower);
                  }
                }}
                loans={loans}
                ariaLabel="Select a Deal"
                placeholder="All Deals"
                enablePlaceholderOption
                boxProps={{ width: '100%' }}
              />

              <ListItem sx={{ flex: 1 }} className="no-pad-sides pad-top-bottom-sm-mobile" layout-align="center center">
                {(company || loan) && (
                  <Box display="flex" className="right" layout-align="center center">
                    <Tooltip title="Clear Filters">
                      <Button sx={{ paddingLeft: '6px' }} onClick={() => resetFilters()}>
                        <NotInterested className="md-icon material-icons pad-left-xs" />
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </ListItem>
            </Box>
          </CardContent>

          {loan && <Headlines loan={loan} />}

          <TransactionsLedger loan={loan} />
        </Card>
      </Box>
    </DesktopLayout>
  );
});
